import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { hasPermission } from "../utils/permissions";
import { hasWhatsAppPermission } from "../utils/whatsappPermissions";
import { getCurrentFinancialYear, getErpToken } from "../erpClient";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../hooks/useIsCurrentFinancialYear";
import "./ProtectedRoute.css";

const ProtectedRoute = ({
  children,
  requiredPermission,
  requiredAnyPermissions,
  requiredWhatsAppPermission,
  /** When true, block create/add pages outside the current financial year. */
  requireCurrentFinancialYear = false,
  currentFinancialYearFallback = "/",
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [hasAccess, setHasAccess] = useState(null);
  const location = useLocation();
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  const fyToastRef = useRef(false);

  const anyKey = Array.isArray(requiredAnyPermissions)
    ? requiredAnyPermissions.join("|")
    : "";

  // Function to check permissions
  const checkPermissions = () => {
    const legacyToken = localStorage.getItem("token");
    const erpToken = getErpToken();
    const token = legacyToken || erpToken;
    const isAuth = !!token;

    setIsAuthenticated(isAuth);

    if (!isAuth) {
      setHasAccess(true);
      return;
    }

    if (requiredWhatsAppPermission) {
      setHasAccess(hasWhatsAppPermission(requiredWhatsAppPermission));
      return;
    }

    if (Array.isArray(requiredAnyPermissions) && requiredAnyPermissions.length > 0) {
      const hasPerm = requiredAnyPermissions.some((p) => hasPermission(p));
      setHasAccess(hasPerm);
      return;
    }

    if (requiredPermission) {
      const hasPerm = hasPermission(requiredPermission);
      setHasAccess(hasPerm);
      return;
    }

    setHasAccess(true);
  };

  useEffect(() => {
    checkPermissions();

    const handlePermissionUpdate = () => {
      checkPermissions();
    };

    window.addEventListener("permissionsUpdated", handlePermissionUpdate);
    window.addEventListener("erpAuthUpdated", handlePermissionUpdate);

    return () => {
      window.removeEventListener("permissionsUpdated", handlePermissionUpdate);
      window.removeEventListener("erpAuthUpdated", handlePermissionUpdate);
    };
  }, [requiredPermission, requiredWhatsAppPermission, anyKey]);

  if (isAuthenticated === null || hasAccess === null) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasAccess) {
    toast.error("You do not have permission to access this page.");
    return <Navigate to="/permissionrestricted" replace />;
  }

  if (
    requireCurrentFinancialYear &&
    getCurrentFinancialYear() != null &&
    !isCurrentFinancialYear
  ) {
    if (!fyToastRef.current) {
      fyToastRef.current = true;
      toast.error(FY_ADD_DISABLED_TITLE);
    }
    return (
      <Navigate to={currentFinancialYearFallback} state={{ from: location }} replace />
    );
  }

  return children;
};

export default ProtectedRoute;
